import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { ControllerProps, FieldErrors, useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { FeatureFlags } from '~constants/featureFlags';
import { ContactItem } from '~hooks/useContact/models/ContactItem';
import { Material } from '~hooks/useMaterials/models';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { useSites } from '~hooks/useSites/useSites';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface HiredTruckLoadProps {
  idx: number;
  isDisabled?: boolean;
  isHiredTruckService: boolean;
  load: HiredTruckLoadValue;
  orderId?: string;
  removeLoad: (idx: number) => void;
}

type SiteContact = Pick<ContactItem, 'name' | 'phone'>;

type WaypointWithContact = {
  contact?: SiteContact | null;
};

type HiredTruckLoadValue = {
  id?: string;
  pickUpWaypoints?: WaypointWithContact[];
  dropOffWaypoints?: WaypointWithContact[];
  _pick_up_waypoints?: WaypointWithContact[];
  _drop_off_waypoints?: WaypointWithContact[];
};

const HiredTruckLoad = ({
  idx,
  isDisabled,
  isHiredTruckService,
  load,
  orderId,
  removeLoad,
}: HiredTruckLoadProps) => {
  const theme = useTheme();
  const { getCompanySitesTypeahead, getSiteContacts } = useSites();
  const { getMaterialsByCompanyIdTypeahead } = useMaterials();
  const loadedRef = useRef(false);

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useFormContext();

  const { userStore } = useStores();
  const watchedCompany = watch('company');
  const companyId = watchedCompany?.id || userStore.userCompany?.id || '';
  // The site typeaheads scope to the hired-truck company above, but the
  // order-scoped materials typeahead expects the acting user's company, same
  // as the other job-scoped material pickers (BulkEditJobDialog, JobEditForm,
  // jobsTableColumns).
  const materialsCompanyId = getEffectiveUserCompanyId(userStore);

  const isStagingWaypointEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.stagingWaypoint,
  });

  const watchedPickUpWaypointSite = watch(`loads[${idx}].pickUpWaypoints[0].site`);
  const watchedDropOffWaypointSite = watch(`loads[${idx}].dropOffWaypoints[0].site`);
  const defaultPickUpWaypoint = load.pickUpWaypoints?.[0] ?? load._pick_up_waypoints?.[0];
  const defaultDropOffWaypoint =
    load.dropOffWaypoints?.[0] ?? load._drop_off_waypoints?.[0];

  const [lastSelectedPickUpPhone, setLastSelectedPickUpPhone] = useState<string>('');
  const [lastSelectedDropOffPhone, setLastSelectedDropOffPhone] = useState<string>('');
  const [pickUpcontacts, setPickUpcontacts] = useState<ContactItem[]>([]);
  const [dropOffcontacts, setDropOffcontacts] = useState<ContactItem[]>([]);
  const [showPickUpContact, setShowPickUpContact] = useState<boolean>(false);
  const [showDropOffContact, setShowDropOffContact] = useState<boolean>(false);
  const [showContacts, setShowContacts] = useState<boolean>(false);

  const toggleOnPickUpContact = () => {
    setShowPickUpContact(true);
  };

  const toggleOffPickUpContact = () => {
    setValue(`loads[${idx}].pickUpWaypoints[0].contact`, {
      name: '',
      phone: '',
    });

    trigger(`loads[${idx}].pickUpWaypoints[0].contact`);
    setShowPickUpContact(false);
  };

  const toggleOnDropOffContact = () => {
    setShowDropOffContact(true);
  };

  const toggleOffDropOffContact = () => {
    setValue(`loads[${idx}].dropOffWaypoints[0].contact`, {
      name: '',
      phone: '',
    });
    trigger(`loads[${idx}].dropOffWaypoints[0].contact`);
    setShowDropOffContact(false);
  };

  const setSiteContact = async (
    waypointType: 'pickUpWayPoint' | 'dropOffWayPoint',
    siteId?: string,
    isDiscarded?: boolean,
  ) => {
    if (siteId && !isDiscarded) {
      const siteContacts = await getSiteContacts({ companyId, siteId });
      if (siteContacts.length === 0) return;

      const isPickup = waypointType === 'pickUpWayPoint';
      const prefix = isPickup ? 'pickUp' : 'dropOff';

      // Set contacts and toggle visibility
      isPickup ? setPickUpcontacts(siteContacts) : setDropOffcontacts(siteContacts);
      isPickup ? toggleOnPickUpContact() : toggleOnDropOffContact();

      // Update form values
      setValue(`loads[${idx}].${prefix}Waypoints[0].contact`, {
        name: siteContacts[0].name,
        phone: siteContacts[0].phone,
      });

      // Update last selected phone
      const setLastPhone = isPickup
        ? setLastSelectedPickUpPhone
        : setLastSelectedDropOffPhone;
      setLastPhone(siteContacts[0].phone || '');
    }
  };

  useEffect(() => {
    if (loadedRef.current) {
      setSiteContact(
        'pickUpWayPoint',
        watchedPickUpWaypointSite?.id,
        Boolean(watchedPickUpWaypointSite?.discardedAt),
      );
    }
  }, [watchedPickUpWaypointSite?.id, watchedPickUpWaypointSite?.discardedAt]);

  useEffect(() => {
    if (loadedRef.current) {
      setSiteContact(
        'dropOffWayPoint',
        watchedDropOffWaypointSite?.id,
        Boolean(watchedDropOffWaypointSite?.discardedAt),
      );
    }
  }, [watchedDropOffWaypointSite?.id, watchedDropOffWaypointSite?.discardedAt]);

  useEffect(() => {
    if (defaultPickUpWaypoint?.contact?.name) {
      setShowPickUpContact(true);
    }
    if (defaultDropOffWaypoint?.contact?.name) {
      setShowDropOffContact(true);
    }
    loadedRef.current = true;
  }, []);

  return (
    <Box key={load?.id}>
      <Grid
        container
        sx={{
          position: 'relative',
          p: 1.5,
          mb: 2,
          border: `1px solid ${theme.brandV2.colors.treadGray7}`,
          borderRadius: 0.5,
        }}
      >
        {isDisabled ? null : (
          <RemoveItemButton
            action={() => removeLoad(idx)}
            sx={{
              ml: 2,
              position: 'absolute',
              top: '-10px',
              right: '-10px',
              cursor: 'pointer',
            }}
          />
        )}

        <Grid size={{ xs: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {idx + 1}
          </Typography>
        </Grid>
        <Grid size={{ xs: 11 }}>
          <Grid container columnSpacing={2}>
            <Grid size={{ xs: 6 }}>
              <AutocompleteAsyncFormField
                asyncCallback={getCompanySitesTypeahead}
                clearable={true}
                control={control}
                disabled={!isHiredTruckService || isDisabled}
                errors={errors}
                extraRequestOptions={{ companyId, onlyActiveSites: true }}
                getLabel={(item) => item.name || ''}
                getValue={(item) => item?.id}
                isRequired={!isHiredTruckService}
                label={`${t('form_fields.pickup')}`}
                name={`loads[${idx}].pickUpWaypoints[0].site`}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <AutocompleteAsyncFormField
                asyncCallback={getCompanySitesTypeahead}
                clearable={true}
                control={control}
                disabled={!isHiredTruckService || isDisabled}
                errors={errors}
                extraRequestOptions={{ companyId, onlyActiveSites: true }}
                getLabel={(material: Material) => {
                  return `${material?.externalId ? `${material.externalId} - ` : ''}${material?.name ?? ''}`;
                }}
                getValue={(item) => item?.id}
                isRequired={!isHiredTruckService}
                label={`${t('form_fields.dropoff')}`}
                name={`loads[${idx}].dropOffWaypoints[0].site`}
              />
            </Grid>
            {isStagingWaypointEnabled && (
              <Grid size={{ xs: 6 }}>
                <AutocompleteAsyncFormField
                  asyncCallback={getCompanySitesTypeahead}
                  clearable={true}
                  control={control}
                  disabled={!isHiredTruckService || isDisabled}
                  errors={errors}
                  extraRequestOptions={{ companyId, onlyActiveSites: true }}
                  getLabel={(item) => item.name || ''}
                  getValue={(item) => item?.id}
                  isRequired={false}
                  label={`${t('form_fields.staging_site')}`}
                  name={`loads[${idx}].stagingWaypoints[0].site`}
                />
              </Grid>
            )}
            {!showContacts && (
              <Grid container size={{ xs: 12 }} sx={{ ml: 2, mb: 2 }}>
                <PseudoLink
                  title={t('order.form.show_contacts')}
                  action={() => setShowContacts(true)}
                />
              </Grid>
            )}
            {showContacts && (
              <Grid
                container
                size={{ xs: 12 }}
                sx={{
                  position: 'relative',
                  p: 1.5,
                  my: 2,
                  border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                  borderRadius: 0.5,
                }}
              >
                <Box
                  sx={{
                    ml: 2,
                    top: '-16px',
                    position: 'absolute',
                    backgroundColor: 'background.paper',
                    p: 0.5,
                  }}
                >
                  <PseudoLink
                    title={t('order.form.hide_contacts')}
                    action={() => setShowContacts(false)}
                  />
                </Box>
                {!showPickUpContact && watchedPickUpWaypointSite && (
                  <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                    <PseudoLink
                      title={`+ ${t('order.form.add_pick_up_site_contact')}`}
                      action={toggleOnPickUpContact}
                    />
                  </Grid>
                )}
                {watchedPickUpWaypointSite && showPickUpContact && (
                  <Grid
                    container
                    size={{ xs: 12 }}
                    sx={{
                      position: 'relative',
                      p: 1.5,
                      mb: 2,
                      border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                      borderRadius: 0.5,
                    }}
                  >
                    <RemoveItemButton
                      action={toggleOffPickUpContact}
                      sx={{
                        ml: 2,
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        cursor: 'pointer',
                      }}
                    />

                    <Grid size={{ xs: 6 }}>
                      <AutocompleteFormField
                        control={control}
                        name={`loads[${idx}].pickUpWaypoints[0].contact.name`}
                        errors={errors}
                        list={pickUpcontacts}
                        label={`${t('order.form.pick_up_site_contact_name')}`}
                        getValue={(item) => item?.name || item}
                        getLabel={(item) => item?.name || item || ''}
                        clearable={true}
                        isRequired={false}
                        freeSolo
                        onSelect={(item) => {
                          setValue(
                            `loads[${idx}].pickUpWaypoints[0].contact.name`,
                            item?.name || item || '',
                          );
                          setValue(
                            `loads[${idx}].pickUpWaypoints[0].contact.phone`,
                            item?.phone || '',
                          );
                          setLastSelectedPickUpPhone(item?.phone || '');
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <PhoneCodeFlagInput
                        key={`pickup-contact-phone-${showPickUpContact}-${lastSelectedPickUpPhone}`}
                        control={control as unknown as ControllerProps['control']}
                        errors={errors as unknown as FieldErrors}
                        name={`loads[${idx}].pickUpWaypoints[0].contact.phone`}
                        label={`${t('form_fields.phone')}`}
                        isRequired={false}
                        sx={{ pl: 2 }}
                      />
                    </Grid>
                  </Grid>
                )}

                {!showDropOffContact && watchedDropOffWaypointSite && (
                  <Grid size={{ xs: 12 }} sx={{ mb: 2, mt: -1 }}>
                    <PseudoLink
                      title={`+ ${t('order.form.add_drop_off_site_contact')}`}
                      action={toggleOnDropOffContact}
                    />
                  </Grid>
                )}
                {watchedDropOffWaypointSite && showDropOffContact && (
                  <Grid
                    container
                    size={{ xs: 12 }}
                    sx={{
                      position: 'relative',
                      p: 1.5,
                      mb: 2,
                      border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                      borderRadius: 0.5,
                    }}
                  >
                    <RemoveItemButton
                      action={toggleOffDropOffContact}
                      sx={{
                        ml: 2,
                        position: 'absolute',
                        top: '-10px',
                        right: '-10px',
                        cursor: 'pointer',
                      }}
                    />
                    <Grid size={{ xs: 6 }}>
                      <AutocompleteFormField
                        control={control}
                        name={`loads[${idx}].dropOffWaypoints[0].contact.name`}
                        errors={errors}
                        list={dropOffcontacts}
                        label={`${t('order.form.drop_off_site_contact_name')}`}
                        getValue={(item) => item?.name || item}
                        getLabel={(item) => item?.name || item || ''}
                        clearable={true}
                        isRequired={false}
                        freeSolo
                        onSelect={(item) => {
                          setValue(
                            `loads[${idx}].dropOffWaypoints[0].contact.name`,
                            item?.name || item || '',
                          );
                          setValue(
                            `loads[${idx}].dropOffWaypoints[0].contact.phone`,
                            item?.phone || '',
                          );
                          setLastSelectedDropOffPhone(item?.phone || '');
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <PhoneCodeFlagInput
                        key={`dropoff-contact-phone-${showDropOffContact}-${lastSelectedDropOffPhone}`}
                        control={control as unknown as ControllerProps['control']}
                        errors={errors as unknown as FieldErrors}
                        name={`loads[${idx}].dropOffWaypoints[0].contact.phone`}
                        label={`${t('form_fields.phone')}`}
                        isRequired={false}
                        sx={{ pl: 2 }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <AutocompleteAsyncFormField
                asyncCallback={getMaterialsByCompanyIdTypeahead}
                clearable={isHiredTruckService}
                control={control}
                disabled={isDisabled}
                errors={errors}
                extraRequestOptions={{ companyId: materialsCompanyId, orderId }}
                getLabel={(material: Material) => material?.name ?? ''}
                getValue={(material: Material) => material?.id ?? ''}
                isRequired={!isHiredTruckService}
                label={`${t('form_fields.material')}`}
                name={`loads[${idx}].material`}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HiredTruckLoad;
